/**
 * NavigatorX Fraud Detection Service
 * 
 * Detects potential referral fraud patterns:
 * - Same device ID across multiple accounts
 * - Same IP address across multiple accounts
 * - Velocity checks (too many referrals in short time)
 * - Suspicious patterns
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  addDoc
} from 'firebase/firestore';
import { navDb } from '../firebase/client';
import { NAV_USERS_COLLECTION } from './navigatorXService';
import type { NavUser } from '../../types/navigatorx';

// ============================================================================
// TYPES
// ============================================================================

export interface FraudAlert {
  id: string;
  type: 'same_device' | 'same_ip' | 'velocity' | 'pattern';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  affectedUsers: string[];
  referrerId?: string;
  detectedAt: Timestamp;
  resolved: boolean;
  resolvedAt?: Timestamp;
  resolvedBy?: string;
  notes?: string;
  metadata: Record<string, any>;
}

export interface FraudStats {
  totalAlerts: number;
  unresolvedAlerts: number;
  alertsByType: Record<string, number>;
  alertsBySeverity: Record<string, number>;
  topSuspiciousReferrers: { userId: string; alertCount: number }[];
  recentAlerts: FraudAlert[];
}

export interface DeviceCluster {
  deviceId: string;
  users: NavUser[];
  isReferralChain: boolean;
}

export interface IPCluster {
  ipAddress: string;
  users: NavUser[];
  isReferralChain: boolean;
}

export interface VelocityCheck {
  userId: string;
  referralCount: number;
  timeWindow: string;
  isAnomalous: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const FRAUD_ALERTS_COLLECTION = 'nav_fraud_alerts';

/** Thresholds */
const SAME_DEVICE_THRESHOLD = 2; // Alert if 2+ accounts share device
const SAME_IP_THRESHOLD = 3; // Alert if 3+ accounts share IP (higher due to NAT)
const VELOCITY_THRESHOLD = 5; // Alert if 5+ referrals in 24 hours
const VELOCITY_WINDOW_HOURS = 24;

// ============================================================================
// DETECTION FUNCTIONS
// ============================================================================

/**
 * Detect accounts sharing the same device ID
 */
export async function detectSameDevice(): Promise<DeviceCluster[]> {
  const usersSnapshot = await getDocs(collection(navDb, NAV_USERS_COLLECTION));
  
  const deviceMap = new Map<string, NavUser[]>();
  
  usersSnapshot.docs.forEach(doc => {
    if (doc.id === 'blocked' || doc.id === 'invites' || doc.id === 'admin_logs') return;
    
    const user = { id: doc.id, ...doc.data() } as NavUser;
    const deviceId = user.deviceId || user.androidId;
    
    if (deviceId) {
      const existing = deviceMap.get(deviceId) || [];
      existing.push(user);
      deviceMap.set(deviceId, existing);
    }
  });

  const clusters: DeviceCluster[] = [];
  
  deviceMap.forEach((users, deviceId) => {
    if (users.length >= SAME_DEVICE_THRESHOLD) {
      // Check if it's a referral chain (one referred the other)
      const isReferralChain = users.some(u1 => 
        users.some(u2 => u1.referredBy === u2.id || u2.referredBy === u1.id)
      );
      
      clusters.push({
        deviceId,
        users,
        isReferralChain
      });
    }
  });

  return clusters;
}

/**
 * Detect accounts sharing the same IP address
 */
export async function detectSameIP(): Promise<IPCluster[]> {
  const usersSnapshot = await getDocs(collection(navDb, NAV_USERS_COLLECTION));
  
  const ipMap = new Map<string, NavUser[]>();
  
  usersSnapshot.docs.forEach(doc => {
    if (doc.id === 'blocked' || doc.id === 'invites' || doc.id === 'admin_logs') return;
    
    const user = { id: doc.id, ...doc.data() } as NavUser;
    const ip = user.lastLoginIP || user.signupIP;
    
    if (ip) {
      const existing = ipMap.get(ip) || [];
      existing.push(user);
      ipMap.set(ip, existing);
    }
  });

  const clusters: IPCluster[] = [];
  
  ipMap.forEach((users, ipAddress) => {
    if (users.length >= SAME_IP_THRESHOLD) {
      const isReferralChain = users.some(u1 => 
        users.some(u2 => u1.referredBy === u2.id || u2.referredBy === u1.id)
      );
      
      clusters.push({
        ipAddress,
        users,
        isReferralChain
      });
    }
  });

  return clusters;
}

/**
 * Detect velocity anomalies (too many referrals in short time)
 */
export async function detectVelocityAnomalies(): Promise<VelocityCheck[]> {
  const usersSnapshot = await getDocs(
    query(
      collection(navDb, NAV_USERS_COLLECTION),
      where('referralCount', '>', 0),
      orderBy('referralCount', 'desc'),
      limit(100)
    )
  );

  const anomalies: VelocityCheck[] = [];
  const windowStart = new Date();
  windowStart.setHours(windowStart.getHours() - VELOCITY_WINDOW_HOURS);

  for (const doc of usersSnapshot.docs) {
    if (doc.id === 'blocked' || doc.id === 'invites' || doc.id === 'admin_logs') continue;
    
    const user = { id: doc.id, ...doc.data() } as NavUser;
    
    // Get referrals in time window
    const referralsSnapshot = await getDocs(
      query(
        collection(navDb, NAV_USERS_COLLECTION, user.id, 'referrals'),
        where('signedUpAt', '>=', Timestamp.fromDate(windowStart)),
        orderBy('signedUpAt', 'desc')
      )
    );

    const recentCount = referralsSnapshot.size;
    
    if (recentCount >= VELOCITY_THRESHOLD) {
      anomalies.push({
        userId: user.id,
        referralCount: recentCount,
        timeWindow: `${VELOCITY_WINDOW_HOURS}h`,
        isAnomalous: true
      });
    }
  }

  return anomalies;
}

/**
 * Detect suspicious patterns in referral chains
 */
export async function detectSuspiciousPatterns(): Promise<{
  selfReferrals: NavUser[];
  circularChains: { users: string[]; pattern: string }[];
  bulkSignups: { time: Date; count: number; users: NavUser[] }[];
}> {
  const usersSnapshot = await getDocs(collection(navDb, NAV_USERS_COLLECTION));
  
  const users: NavUser[] = [];
  usersSnapshot.docs.forEach(doc => {
    if (doc.id === 'blocked' || doc.id === 'invites' || doc.id === 'admin_logs') return;
    users.push({ id: doc.id, ...doc.data() } as NavUser);
  });

  // Self-referrals (shouldn't be possible but check)
  const selfReferrals = users.filter(u => u.referredBy === u.id);

  // Circular chains (A → B → A or A → B → C → A)
  const circularChains: { users: string[]; pattern: string }[] = [];
  
  for (const user of users) {
    if (!user.referredBy) continue;
    
    const chain: string[] = [user.id];
    let current = user.referredBy;
    let depth = 0;
    
    while (current && depth < 10) {
      if (chain.includes(current)) {
        circularChains.push({
          users: [...chain, current],
          pattern: chain.join(' → ') + ' → ' + current
        });
        break;
      }
      chain.push(current);
      const referrer = users.find(u => u.id === current);
      current = referrer?.referredBy;
      depth++;
    }
  }

  // Bulk signups (many accounts created within minutes)
  const bulkSignups: { time: Date; count: number; users: NavUser[] }[] = [];
  
  const sortedByCreation = [...users]
    .filter(u => u.createdAt)
    .sort((a, b) => {
      const timeA = a.createdAt instanceof Timestamp ? a.createdAt.toMillis() : 0;
      const timeB = b.createdAt instanceof Timestamp ? b.createdAt.toMillis() : 0;
      return timeA - timeB;
    });

  for (let i = 0; i < sortedByCreation.length; i++) {
    const windowUsers: NavUser[] = [sortedByCreation[i]];
    const baseTime = sortedByCreation[i].createdAt instanceof Timestamp 
      ? sortedByCreation[i].createdAt.toMillis() 
      : 0;
    
    for (let j = i + 1; j < sortedByCreation.length; j++) {
      const compareTime = sortedByCreation[j].createdAt instanceof Timestamp 
        ? sortedByCreation[j].createdAt.toMillis() 
        : 0;
      
      if (compareTime - baseTime <= 5 * 60 * 1000) { // 5 minutes
        windowUsers.push(sortedByCreation[j]);
      } else {
        break;
      }
    }
    
    if (windowUsers.length >= 5) {
      bulkSignups.push({
        time: new Date(baseTime),
        count: windowUsers.length,
        users: windowUsers
      });
      i += windowUsers.length - 1; // Skip processed users
    }
  }

  return { selfReferrals, circularChains, bulkSignups };
}

// ============================================================================
// ALERT MANAGEMENT
// ============================================================================

/**
 * Create a fraud alert
 */
export async function createFraudAlert(
  alert: Omit<FraudAlert, 'id' | 'detectedAt' | 'resolved'>
): Promise<string> {
  const docRef = await addDoc(collection(navDb, FRAUD_ALERTS_COLLECTION), {
    ...alert,
    detectedAt: Timestamp.now(),
    resolved: false
  });
  return docRef.id;
}

/**
 * Get all fraud alerts
 */
export async function getFraudAlerts(unresolvedOnly: boolean = false): Promise<FraudAlert[]> {
  let q = query(
    collection(navDb, FRAUD_ALERTS_COLLECTION),
    orderBy('detectedAt', 'desc'),
    limit(100)
  );

  if (unresolvedOnly) {
    q = query(q, where('resolved', '==', false));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as FraudAlert[];
}

/**
 * Get fraud statistics
 */
export async function getFraudStats(): Promise<FraudStats> {
  const alerts = await getFraudAlerts();
  
  const alertsByType: Record<string, number> = {};
  const alertsBySeverity: Record<string, number> = {};
  const referrerAlertCounts: Record<string, number> = {};

  alerts.forEach(alert => {
    alertsByType[alert.type] = (alertsByType[alert.type] || 0) + 1;
    alertsBySeverity[alert.severity] = (alertsBySeverity[alert.severity] || 0) + 1;
    
    if (alert.referrerId) {
      referrerAlertCounts[alert.referrerId] = (referrerAlertCounts[alert.referrerId] || 0) + 1;
    }
  });

  const topSuspiciousReferrers = Object.entries(referrerAlertCounts)
    .map(([userId, alertCount]) => ({ userId, alertCount }))
    .sort((a, b) => b.alertCount - a.alertCount)
    .slice(0, 10);

  return {
    totalAlerts: alerts.length,
    unresolvedAlerts: alerts.filter(a => !a.resolved).length,
    alertsByType,
    alertsBySeverity,
    topSuspiciousReferrers,
    recentAlerts: alerts.slice(0, 10)
  };
}

/**
 * Run full fraud scan and generate alerts
 */
export async function runFraudScan(): Promise<{
  deviceClusters: number;
  ipClusters: number;
  velocityAnomalies: number;
  patterns: number;
  alertsCreated: number;
}> {
  let alertsCreated = 0;

  // Device clusters
  const deviceClusters = await detectSameDevice();
  for (const cluster of deviceClusters.filter(c => c.isReferralChain)) {
    await createFraudAlert({
      type: 'same_device',
      severity: 'high',
      description: `${cluster.users.length} accounts sharing device ${cluster.deviceId.substring(0, 8)}...`,
      affectedUsers: cluster.users.map(u => u.id),
      metadata: { deviceId: cluster.deviceId }
    });
    alertsCreated++;
  }

  // IP clusters
  const ipClusters = await detectSameIP();
  for (const cluster of ipClusters.filter(c => c.isReferralChain)) {
    await createFraudAlert({
      type: 'same_ip',
      severity: 'medium',
      description: `${cluster.users.length} accounts in referral chain sharing IP ${cluster.ipAddress}`,
      affectedUsers: cluster.users.map(u => u.id),
      metadata: { ipAddress: cluster.ipAddress }
    });
    alertsCreated++;
  }

  // Velocity
  const velocityAnomalies = await detectVelocityAnomalies();
  for (const anomaly of velocityAnomalies) {
    await createFraudAlert({
      type: 'velocity',
      severity: 'high',
      description: `User has ${anomaly.referralCount} referrals in ${anomaly.timeWindow}`,
      affectedUsers: [anomaly.userId],
      referrerId: anomaly.userId,
      metadata: { referralCount: anomaly.referralCount, timeWindow: anomaly.timeWindow }
    });
    alertsCreated++;
  }

  // Patterns
  const patterns = await detectSuspiciousPatterns();
  
  for (const user of patterns.selfReferrals) {
    await createFraudAlert({
      type: 'pattern',
      severity: 'critical',
      description: `Self-referral detected for user ${user.id}`,
      affectedUsers: [user.id],
      referrerId: user.id,
      metadata: { pattern: 'self_referral' }
    });
    alertsCreated++;
  }

  for (const chain of patterns.circularChains) {
    await createFraudAlert({
      type: 'pattern',
      severity: 'critical',
      description: `Circular referral chain detected: ${chain.pattern}`,
      affectedUsers: chain.users,
      metadata: { pattern: 'circular_chain', chain: chain.pattern }
    });
    alertsCreated++;
  }

  for (const bulk of patterns.bulkSignups) {
    await createFraudAlert({
      type: 'pattern',
      severity: 'medium',
      description: `${bulk.count} accounts created within 5 minutes at ${bulk.time.toISOString()}`,
      affectedUsers: bulk.users.map(u => u.id),
      metadata: { pattern: 'bulk_signup', count: bulk.count, time: bulk.time.toISOString() }
    });
    alertsCreated++;
  }

  return {
    deviceClusters: deviceClusters.length,
    ipClusters: ipClusters.length,
    velocityAnomalies: velocityAnomalies.length,
    patterns: patterns.selfReferrals.length + patterns.circularChains.length + patterns.bulkSignups.length,
    alertsCreated
  };
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

const navigatorXFraudService = {
  // Detection
  detectSameDevice,
  detectSameIP,
  detectVelocityAnomalies,
  detectSuspiciousPatterns,
  
  // Alerts
  createFraudAlert,
  getFraudAlerts,
  getFraudStats,
  runFraudScan,
  
  // Constants
  SAME_DEVICE_THRESHOLD,
  SAME_IP_THRESHOLD,
  VELOCITY_THRESHOLD
};

export default navigatorXFraudService;
